import React, { FunctionComponent, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Dispatch } from "type_aliases";
import { ReduxStore } from "../../services/reducers/root_reducer";
import useLatestTos from "../../queries/use_latest_tos";
import { SanitizedHtml } from "../sanitized_html";
import acceptTos from "../../actions/confirm_account_page/accept_tos";
import useTosAcceptances from "../../queries/use_tos_acceptances";
import { useFlags } from "../../services/utilities/launch_darkly_helper";
import useUserAccount from "../../queries/user/use_user_account";
import DateConverter from "../../services/converters/date_converter";
import { NovaTheme } from "@hxnova/themes";
import { Button, Dialog, ThemeProvider } from "@hxnova/react-components";
import "../../../css/dialogs/terms_of_service_dialog.css";

export type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>

export const TermsOfServiceDialog: FunctionComponent<Props> = function TermsOfServiceDialog(props) {
  const {
    acceptTos,
  } = props;

  const { termsOfServiceRequired } = useFlags();
  const { data: latestTos } = useLatestTos();
  const { data: tosAcceptances } = useTosAcceptances();
  const { data: userAccount } = useUserAccount();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [bottom, setBottom] = React.useState(false);
  const [agreed, setAgreed] = React.useState(false);

  useEffect(() => {
    if (!userAccount?.account?.createdAt) {
      return;
    }
    const createdAt = DateConverter.toDate(userAccount.account.createdAt);
    let mustAcceptTos: boolean;
    try {
      mustAcceptTos = DateConverter.isOnOrAfterDay(termsOfServiceRequired, createdAt)
    } catch (e) {
      mustAcceptTos = false;
    }
    setDialogOpen(mustAcceptTos && tosAcceptances.length === 0 && !!latestTos?.content);
  }, [tosAcceptances, termsOfServiceRequired, userAccount, latestTos]);

  useEffect(() => {
    const scrollableContent = document.querySelector("#TermsOfServiceDialog-scrollableContent");
    const endingMarker = document.querySelector("#TermsOfServiceDialog-endingMarker");
    if (scrollableContent == null || endingMarker == null) {
      return;
    }

    const handleIntersect = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          setBottom(true);
        }
      }
    };

    const observer = new IntersectionObserver(handleIntersect, {
      root: scrollableContent,
      // @ts-ignore
      scrollMargin: "200px",
    });
    observer.observe(endingMarker);
  });

  const agree = async () => {
    await acceptTos(latestTos.id);
    setAgreed(true);
  };

  return agreed ? null
  : <ThemeProvider theme={NovaTheme} defaultMode="light" storageManager={null}>
    <Dialog.Root
      open={dialogOpen}
      aria-labelledby="terms-of-service-dialog-title"
      className="TermsOfServiceDialog"
    >
      <Dialog.Header id="terms-of-service-dialog-title" className="TermsOfServiceDialog-title">
        Terms of Service
      </Dialog.Header>
      <Dialog.Content className="TermsOfServiceDialog-subtext">Read the terms below to continue.</Dialog.Content>
      <Dialog.Content id="TermsOfServiceDialog-scrollableContent" className="TermsOfServiceDialog-content">
        <SanitizedHtml id="TermsOfServiceDialog-content" content={latestTos?.content}/>
        <div id="TermsOfServiceDialog-endingMarker"></div>
      </Dialog.Content>
      { bottom ? null :
        <Dialog.Content className="TermsOfServiceDialog-pre-actions">
          Scroll to the bottom of the terms to continue.
        </Dialog.Content>
      }
      <Dialog.Actions className="TermsOfServiceDialog-actions">
        <Button
          onClick={agree}
          disabled={!bottom}
          color="primary">
          I Agree
        </Button>
      </Dialog.Actions>
    </Dialog.Root>
  </ThemeProvider>;
};

const mapStateToProps = (state: ReduxStore, props) => ({
});

type DispatchedEvents = void;

const mapDispatchToProps = (dispatch: Dispatch<DispatchedEvents>) => bindActionCreators({
  acceptTos,
}, dispatch);

export default connect<ReturnType<typeof mapStateToProps>, ReturnType<typeof mapDispatchToProps>>(mapStateToProps, mapDispatchToProps)(TermsOfServiceDialog);
